'use client';

import { useEffect, useState } from 'react';

import { toFriendly } from 'src/lib/lang/time';

interface RelativeTimeProps {
  /**
   * 时间戳 - 可以是数字、字符串或 Date 对象
   */
  timestamp?: number | string | Date;
  /**
   * 是否自动刷新 - 默认 true,每分钟更新一次
   */
  autoRefresh?: boolean;
  /**
   * 刷新间隔(毫秒) - 默认 60000 (1分钟)
   */
  refreshInterval?: number;
  /**
   * 空值时显示的占位符 - 默认 '--'
   */
  placeholder?: string;
}

/**
 * 相对时间显示组件
 *
 * 用于避免 SSR/CSR 水合错误的客户端专用时间组件。
 * 在服务端渲染时显示占位符,客户端 mount 后才计算并显示相对时间。
 *
 * @example
 * ```tsx
 * <RelativeTime timestamp={1700123456789} />
 * // 显示: "2小时前"
 *
 * <RelativeTime timestamp={Date.now()} autoRefresh={true} />
 * // 显示: "刚刚" (并且每分钟自动更新)
 * ```
 */
export function RelativeTime({
  timestamp,
  autoRefresh = true,
  refreshInterval = 60000,
  placeholder = '--',
}: RelativeTimeProps) {
  const [timeText, setTimeText] = useState<string>(placeholder);

  useEffect(() => {
    if (!timestamp) {
      setTimeText(placeholder);
      return;
    }

    // 计算相对时间
    const updateTime = () => {
      try {
        setTimeText(toFriendly(timestamp));
      } catch (error) {
        console.error('[RelativeTime] Failed to format time:', error);
        setTimeText(placeholder);
      }
    };

    // 立即更新一次
    updateTime();

    // 如果启用自动刷新,设置定时器
    if (!autoRefresh) return;

    const interval = setInterval(updateTime, refreshInterval);

    return () => clearInterval(interval);
  }, [timestamp, autoRefresh, refreshInterval, placeholder]);

  return <span>{timeText}</span>;
}
